import React, { useState } from 'react';
import axios from 'axios';
import { X, ShoppingBag, Trash2, ArrowLeft, MapPin, ScanLine } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import PhoneVerifyInline from './PhoneVerifyInline';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/use-toast';
import BarcodeScannerModal from './BarcodeScannerModal';
import Rial from './Rial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Oman VAT rate (matches backend env default `VAT_RATE`). The server is
// authoritative on submit — this is purely an order-summary display estimate.
const VAT_RATE = 0.05;

const CartDrawer = () => {
  const { cart, setQty, remove, total, count, open, setOpen, clear } = useCart();
  const { email: customerEmail } = useCustomerAuth();
  const { toast } = useToast();
  const [step, setStep] = useState('cart'); // cart | checkout
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', customer_email: '', customer_address: '', customer_business_name: '', notes: '' });
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Existing-vs-new customer flow. Existing customers enter their 6-digit
  // customer number (issued on their first order) and we auto-fill the rest
  // — saves them re-typing name/phone/email on every B2B order.
  const [customerMode, setCustomerMode] = useState('new'); // 'new' | 'existing'
  const [customerNumber, setCustomerNumber] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupOk, setLookupOk] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Pre-fill email if customer is logged in
  React.useEffect(() => {
    if (customerEmail && !form.customer_email) {
      setForm((f) => ({ ...f, customer_email: customerEmail }));
    }
  }, [customerEmail]);

  // Auto-lookup as soon as the customer number reaches 8 digits — saves a
  // tap and matches the scanner UX (which auto-triggers anyway). The
  // onChange handler resets `lookupOk` when the input changes, so this
  // effect only fires once per completed number.
  React.useEffect(() => {
    if (
      customerMode === 'existing' &&
      customerNumber.length === 6 &&
      !lookingUp &&
      !lookupOk
    ) {
      lookupCustomer(customerNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerMode, customerNumber, lookupOk]);

  // Lock background scroll + freeze the underlying page in place while the
  // drawer is open. Without this, iOS Safari lets the touch-scroll bleed
  // through onto the storefront behind, which manifests as the page
  // appearing to "zoom out" / shrink when the cart slides in. Restoring the
  // scrollTop on close avoids the page snapping back to the top.
  React.useEffect(() => {
    if (!open) return;
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!open) return null;

  // VAT is applied only on items flagged `vat_applicable` by the admin in the
  // products screen. Computed live so the customer sees the breakdown before
  // submitting. Backend re-computes authoritatively at checkout.
  const subtotal = total;
  const vat = cart.reduce((s, c) => (c.vat_applicable ? s + c.price * c.qty : s), 0) * VAT_RATE;
  const grandTotal = subtotal + vat;

  const lookupCustomer = async (override) => {
    const cn = (override || customerNumber).trim();
    if (cn.length !== 6) return;
    setLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/customers/lookup/${cn}`);
      setForm((f) => ({
        ...f,
        customer_name: data.name || f.customer_name,
        customer_phone: data.phone || f.customer_phone,
        customer_email: data.email || f.customer_email,
        customer_address: data.address || f.customer_address,
        customer_business_name: data.business_name || f.customer_business_name || '',
      }));
      setCustomerNumber(cn);
      setLookupOk(true);
      // Surface the business name in the welcome toast if present — primary
      // signal that the right account loaded after a barcode scan.
      const who = (data.business_name || data.name || '').split(' ').slice(0, 4).join(' ') || 'there';
      toast({ title: `Welcome back, ${who}!`, description: data.business_name ? `Loaded customer #${cn} — ${data.business_name}` : 'We\'ve filled in your details.' });
    } catch (err) {
      setLookupOk(false);
      toast({
        title: 'Customer not found',
        description: err?.response?.data?.detail || 'Check the number or switch to New customer.',
      });
    } finally {
      setLookingUp(false);
    }
  };

  const onScanResult = (text) => {
    setScannerOpen(false);
    // Accept any all-digit code; trim to last 6 digits (handles wrappers like CODE128-Cset prefix).
    const digits = (text || '').replace(/\D/g, '');
    if (digits.length < 6) {
      toast({ title: 'Unsupported barcode', description: 'That barcode does not contain a 6-digit customer number.' });
      return;
    }
    const cn = digits.slice(-6);
    setCustomerNumber(cn);
    lookupCustomer(cn);
  };

  const closeAll = () => {
    setOpen(false);
    setTimeout(() => setStep('cart'), 300);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.customer_email) {
      toast({ title: 'Missing details', description: 'Name, phone and email are required.' });
      return;
    }
    if (!('geolocation' in navigator)) {
      toast({
        title: 'Location not supported',
        description: 'Your browser cannot share its location. We need it for delivery.',
      });
      return;
    }
    setSubmitting(true);
    try {
      // FORCED: capture a fresh, accurate GPS fix at checkout time. The order
      // can only be placed once the customer grants location permission and a
      // reading is available — this guarantees every order arrives with a
      // verifiable delivery pin.
      let loc;
      try {
        loc = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) =>
              resolve({
                location_lat: pos.coords.latitude,
                location_lng: pos.coords.longitude,
                location_accuracy: pos.coords.accuracy,
              }),
            (err) => reject(err),
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
          );
        });
      } catch (err) {
        setSubmitting(false);
        const code = err && err.code;
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        const msg =
          code === 1
            ? 'Please allow location access — we need it to deliver your order. Tap the location icon in your browser address bar to enable it, then try again.'
            : code === 3
            ? 'Could not get an accurate location. Step near a window or outside and try again.'
            : 'Your device could not share its location. Please enable GPS / Location services and try again.';
        toast({ title: 'Location required', description: msg });
        return;
      }

      // Best-effort: also persist the fresh location to the customer profile
      try {
        localStorage.setItem(
          'mr_customer_loc',
          JSON.stringify({
            lat: loc.location_lat,
            lng: loc.location_lng,
            accuracy: loc.location_accuracy,
          }),
        );
      } catch {
        // ignore
      }

      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        customer_address: form.customer_address,
        customer_business_name: form.customer_business_name || '',
        notes: form.notes,
        items: cart.map((c) => ({
          id: c.id,
          name: c.name,
          size: c.size || '',
          size_type: c.size_type || 'size',
          price: c.price,
          qty: c.qty,
          image: c.image || '',
          uom: c.uom || 'ctn',
        })),
        ...loc,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      // Highlight the customer number if this is the customer's first order so
      // they know to reuse it next time.
      const cnumLine = data.customer_number
        ? `Your customer number: ${data.customer_number} — save it to skip checkout next time.`
        : '';
      toast({
        title: 'Order placed — Thank you!',
        description: `Order ${data.order_number} for OMR ${data.total.toFixed(3)} received.${cnumLine ? ' ' + cnumLine : ' Track it under "My orders".'}`,
        duration: 8000,
      });
      clear();
      setForm({ customer_name: '', customer_phone: '', customer_email: form.customer_email, customer_address: '', customer_business_name: '', notes: '' });
      closeAll();
    } catch (err) {
      toast({
        title: 'Order failed',
        description: err.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeAll} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2">
            {step === 'checkout' && (
              <button onClick={() => setStep('cart')} className="p-1 hover:bg-neutral-100 rounded-full mr-1">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <ShoppingBag className="w-5 h-5 text-[#0078D4]" />
            {step === 'cart' ? `Your cart (${count})` : 'Delivery details'}
          </h3>
          <button onClick={closeAll} className="p-2 hover:bg-neutral-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'cart' ? (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#DEECF9] flex items-center justify-center mb-4">
                    <ShoppingBag className="w-7 h-7 text-[#0078D4]" />
                  </div>
                  <p className="text-neutral-500">Your cart is empty.</p>
                  <p className="text-xs text-neutral-400 mt-1">Add items from any division to get started.</p>
                </div>
              ) : (
                cart.map((c) => {
                  const uom = c.uom || 'ctn';
                  const minQty = c.min_qty ?? 1;
                  return (
                    <div key={c.id} className="flex gap-3 pb-4 border-b border-neutral-100 last:border-0">
                      <div className="w-20 h-20 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: c.bg || '#F8FAFC' }}>
                        <img src={c.image} alt={c.name} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm leading-snug">{c.name}</div>
                        {c.size && <div className="text-xs text-neutral-500">{c.size}</div>}
                        <div className="text-sm font-semibold text-[#0078D4] mt-1">
                          <Rial /> {(c.price * c.qty).toFixed(3)}
                          <span className="ml-1 text-[11px] font-medium text-neutral-500">
                            ({c.qty} × <Rial /> {c.price.toFixed(3)})
                          </span>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="inline-flex items-center gap-1.5 bg-neutral-100 border border-neutral-200 rounded-lg px-2.5 h-9">
                            <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide">Qty</label>
                            <Input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              autoComplete="off"
                              value={c.qty}
                              onChange={(e) => {
                                const v = e.target.value.replace(/[^\d]/g, '');
                                setQty(c.id, v === '' ? 1 : parseInt(v, 10));
                              }}
                              onBlur={(e) => {
                                const n = parseInt(e.target.value, 10);
                                if (!Number.isFinite(n) || n < minQty) setQty(c.id, minQty);
                              }}
                              className="h-7 w-20 px-1 text-base font-bold text-center bg-white border-neutral-200 text-[#0F172A]"
                              data-testid={`cart-qty-${c.id}`}
                            />
                            <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide">
                              {uom}
                            </span>
                          </div>
                          <button onClick={() => remove(c.id)} className="text-neutral-400 hover:text-red-500 p-1" data-testid={`cart-remove-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {cart.length > 0 && (
              <div className="p-5 border-t border-neutral-200 bg-[#F8FAFC]">
                <div className="flex justify-between mb-1 text-sm text-neutral-600">
                  <span>Items</span>
                  <span>{count}</span>
                </div>
                <div className="flex justify-between text-sm text-neutral-700">
                  <span>Subtotal</span>
                  <span><Rial /> {subtotal.toFixed(3)}</span>
                </div>
                {vat > 0 && (
                  <div className="flex justify-between text-sm text-neutral-700 mb-1" data-testid="cart-vat-row-summary">
                    <span>VAT (5%)</span>
                    <span><Rial /> {vat.toFixed(3)}</span>
                  </div>
                )}
                <div className="flex justify-between mb-4 mt-1 pt-2 border-t border-neutral-200">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-xl" data-testid="cart-summary-total"><Rial /> {grandTotal.toFixed(3)}</span>
                </div>
                <Button
                  onClick={() => setStep('checkout')}
                  className="w-full h-12 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold btn-lift"
                >
                  Checkout — <Rial /> {grandTotal.toFixed(3)}
                </Button>
                <p className="text-xs text-neutral-500 text-center mt-3">
                  Free delivery in Muscat on orders over <Rial /> 5.000
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <form id="checkout-form" onSubmit={placeOrder} className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Existing vs new customer */}
              <div className="rounded-2xl bg-[#F8FAFC] border border-neutral-200 p-3" data-testid="customer-mode">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'new', label: 'New customer' },
                    { id: 'existing', label: 'Existing customer' },
                  ].map((opt) => {
                    const active = customerMode === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          setCustomerMode(opt.id);
                          if (opt.id === 'new') {
                            setLookupOk(false);
                            setCustomerNumber('');
                          }
                        }}
                        className={`h-10 rounded-full text-sm font-semibold transition ${
                          active
                            ? 'bg-[#0078D4] text-white shadow'
                            : 'bg-white text-neutral-700 border border-neutral-200 hover:border-[#0078D4]/40'
                        }`}
                        data-testid={`customer-mode-${opt.id}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {customerMode === 'existing' && (
                  <div className="mt-3" data-testid="customer-number-block">
                    <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">
                      Customer code
                    </label>
                    <div className="mt-1 flex gap-2">
                      <Input
                        maxLength={50}
                        value={customerNumber}
                        onChange={(e) => {
                          // Feb 2026 — accepts alphanumeric, spaces and
                          // hyphens up to 50 chars. Strip anything else
                          // as the user types so paste stays clean.
                          const v = e.target.value.replace(/[^A-Za-z0-9 \-]/g, '').slice(0, 50);
                          setCustomerNumber(v);
                          setLookupOk(false);
                        }}
                        placeholder="100001 or CUST-001"
                        className="h-11 flex-1 font-mono tracking-wider"
                        data-testid="customer-number-input"
                      />
                      <Button
                        type="button"
                        onClick={() => setScannerOpen(true)}
                        className="h-11 w-11 p-0 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                        title="Scan barcode"
                        data-testid="customer-number-scan"
                      >
                        <ScanLine className="w-5 h-5" />
                      </Button>
                    </div>
                    {lookingUp && (
                      <p className="text-[11px] text-neutral-500 mt-1.5 inline-flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-[#0078D4] animate-pulse" />
                        Looking you up…
                      </p>
                    )}
                    {lookupOk && (
                      <p className="text-[11px] text-emerald-700 mt-1.5">
                        ✓ Found — your details have been filled in. Tap "Place order" below.
                      </p>
                    )}
                    <p className="text-[11px] text-neutral-500 mt-1.5">
                      Don't have one? Pick "New customer" — we'll issue you a number after your first order.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Business name <span className="font-normal text-neutral-400">(optional)</span></label>
                <Input
                  readOnly={lookupOk}
                  value={form.customer_business_name}
                  onChange={(e) => setForm({ ...form, customer_business_name: e.target.value })}
                  placeholder="ABC Trading LLC"
                  className={`mt-1 h-11 ${lookupOk ? '!bg-slate-200 !text-slate-600 !border-slate-300 cursor-not-allowed pointer-events-none' : ''}`}
                  data-testid="checkout-customer-business-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Full name *</label>
                <Input
                  required
                  readOnly={lookupOk}
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="Ahmed Al Balushi"
                  className={`mt-1 h-11 ${lookupOk ? '!bg-slate-200 !text-slate-600 !border-slate-300 cursor-not-allowed pointer-events-none' : ''}`}
                  data-testid="checkout-customer-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Phone *</label>
                <Input
                  required
                  readOnly={lookupOk}
                  type="tel"
                  value={form.customer_phone}
                  onChange={(e) => { setForm({ ...form, customer_phone: e.target.value }); setPhoneVerified(false); }}
                  placeholder="+968 9XXX XXXX"
                  className={`mt-1 h-11 ${lookupOk ? '!bg-slate-200 !text-slate-600 !border-slate-300 cursor-not-allowed pointer-events-none' : ''}`}
                  data-testid="checkout-customer-phone"
                />
                {/* New-customer phone OTP verification. Server enforces it when
                    `notifications.require_phone_verify_at_checkout` is ON. */}
                <PhoneVerifyInline
                  phone={form.customer_phone}
                  email={form.customer_email}
                  verified={phoneVerified}
                  onVerified={() => setPhoneVerified(true)}
                  testId="checkout-phone-verify"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Email *</label>
                <Input
                  required
                  readOnly={lookupOk}
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  placeholder="your@email.com"
                  className={`mt-1 h-11 ${lookupOk ? '!bg-slate-200 !text-slate-600 !border-slate-300 cursor-not-allowed pointer-events-none' : ''}`}
                  data-testid="checkout-customer-email"
                />
                {!lookupOk && (
                  <p className="text-[11px] text-neutral-500 mt-1">
                    Use this email later to sign in and download invoices.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Delivery address</label>
                <Textarea
                  rows={2}
                  readOnly={lookupOk}
                  value={form.customer_address}
                  onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                  placeholder="Building, street, area, city"
                  className={`mt-1 ${lookupOk ? '!bg-slate-200 !text-slate-600 !border-slate-300 cursor-not-allowed pointer-events-none' : ''}`}
                  data-testid="checkout-customer-address"
                />
              </div>
              {lookupOk && (
                <div className="-mt-2 flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800">
                  <span>Linked to your saved profile. To change, switch back to <b>New customer</b>.</span>
                  <button
                    type="button"
                    onClick={() => {
                      setLookupOk(false);
                      setCustomerMode('new');
                      setCustomerNumber('');
                    }}
                    className="text-emerald-700 font-semibold underline ml-2"
                    data-testid="checkout-unlock-fields"
                  >
                    Change
                  </button>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Notes (optional)</label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any special instructions?"
                  className="mt-1"
                />
              </div>

              <div className="mt-4 p-4 rounded-xl bg-[#FFF7ED] border border-[#FED7AA] flex items-start gap-3" data-testid="checkout-location-notice">
                <MapPin className="w-5 h-5 text-[#EA580C] mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-[#9A3412]">Location required for delivery</div>
                  <div className="text-xs text-[#9A3412]/80 mt-0.5">
                    Tapping "Place order" will ask your device for its current GPS location so our driver can find you. Please allow it.
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 rounded-xl bg-[#DEECF9]">
                <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-2">Order summary</div>
                {cart.map((c) => (
                  <div key={c.id} className="flex justify-between text-sm py-0.5">
                    <span>{c.qty} {c.uom || 'ctn'} × {c.name}</span>
                    <span className="font-medium"><Rial /> {(c.price * c.qty).toFixed(3)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#0078D4]/30 text-sm">
                  <span className="text-neutral-700">Subtotal</span>
                  <span className="font-medium"><Rial /> {subtotal.toFixed(3)}</span>
                </div>
                <div className="flex justify-between text-sm py-0.5" data-testid="cart-vat-row">
                  <span className="text-neutral-700">VAT (5%)</span>
                  <span className="font-medium"><Rial /> {vat.toFixed(3)}</span>
                </div>
                <div className="flex justify-between pt-2 mt-1 border-t border-[#0078D4]/30 font-bold">
                  <span>Total</span>
                  <span data-testid="cart-grand-total"><Rial /> {grandTotal.toFixed(3)}</span>
                </div>
              </div>
            </form>
            <div className="p-5 border-t border-neutral-200">
              <Button
                form="checkout-form"
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold btn-lift"
              >
                {submitting ? 'Placing order…' : <>Place order — <Rial /> {grandTotal.toFixed(3)}</>}
              </Button>
              <p className="text-xs text-neutral-500 text-center mt-3">
                Pay on delivery. We'll call to confirm.
              </p>
            </div>
          </>
        )}
      </div>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={onScanResult}
        helperText="Hold your customer card barcode inside the box"
      />
    </div>
  );
};

export default CartDrawer;
